import React, { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { useLabProgress } from '../../hooks/useLabProgress';

import {
  ArrowLeft, Star, Check, Lock, ChevronRight, RotateCcw,
  Info, Building2, TreePine, Home, Factory, Zap, GraduationCap,
  Heart as HeartIcon, Bus, Leaf, Trash2
} from 'lucide-react';

const TILE_TYPES = {
  empty:      { label: 'Empty', color: '#1a1a2e', icon: null, cost: 0, happiness: 0, pollution: 0, income: 0, power: 0 },
  road:       { label: 'Road', color: '#475569', icon: null, cost: 50, happiness: 0, pollution: 0, income: 0, power: 0 },
  house:      { label: 'House', color: '#3b82f6', icon: Home, cost: 200, happiness: 5, pollution: 1, income: 100, power: -2 },
  apartment:  { label: 'Apartment', color: '#6366f1', icon: Building2, cost: 500, happiness: 3, pollution: 2, income: 300, power: -5 },
  shop:       { label: 'Shop', color: '#f59e0b', icon: Building2, cost: 300, happiness: 4, pollution: 1, income: 200, power: -1 },
  factory:    { label: 'Factory', color: '#78716c', icon: Factory, cost: 800, happiness: -5, pollution: 10, income: 500, power: -10 },
  park:       { label: 'Park', color: '#22c55e', icon: TreePine, cost: 150, happiness: 10, pollution: -3, income: 0, power: 0 },
  school:     { label: 'School', color: '#8b5cf6', icon: GraduationCap, cost: 400, happiness: 8, pollution: 0, income: 0, power: -3 },
  hospital:   { label: 'Hospital', color: '#ef4444', icon: HeartIcon, cost: 600, happiness: 7, pollution: 0, income: 0, power: -5 },
  powerplant: { label: 'Power Plant', color: '#eab308', icon: Zap, cost: 1000, happiness: -3, pollution: 8, income: 0, power: 50 },
  bus_stop:   { label: 'Bus Stop', color: '#06b6d4', icon: Bus, cost: 100, happiness: 3, pollution: -1, income: 0, power: 0 },
  solar:      { label: 'Solar Farm', color: '#10b981', icon: Leaf, cost: 700, happiness: 2, pollution: -5, income: 0, power: 30 },
};

const GRID_SIZE = 8;

const MISSIONS = [
  { id: 1, title: 'First Neighborhood', difficulty: 1, description: 'Build 3 houses and 1 road. Every city starts small!',
    goal: (g) => countType(g, 'house') >= 3 && countType(g, 'road') >= 1,
    fact: 'The first planned city was Mohenjo-daro (2500 BCE) with grid streets, drainage, and public baths!' },
  { id: 2, title: 'Power Up!', difficulty: 1, description: 'Build a power plant — your city needs energy to grow!',
    goal: (g) => getStats(g).power >= 0 && (countType(g, 'powerplant') >= 1 || countType(g, 'solar') >= 1),
    fact: 'NYC uses about 11,000 MW of power daily. A single solar panel on every rooftop could supply 25% of that!' },
  { id: 3, title: 'Commercial District', difficulty: 1, description: 'Add 3 shops to create a commercial area. Citizens need places to buy goods!',
    goal: (g) => countType(g, 'shop') >= 3,
    fact: 'The Grand Bazaar in Istanbul is one of the oldest covered markets (1455) with over 4,000 shops!' },
  { id: 4, title: 'Industrial Zone', difficulty: 2, description: 'Build 2 factories but keep pollution below 30!',
    goal: (g) => countType(g, 'factory') >= 2 && getStats(g).pollution < 30,
    fact: 'Industrial zones are usually downwind from residential areas. Zoning laws were invented in NYC in 1916!' },
  { id: 5, title: 'Green Spaces', difficulty: 1, description: 'Add 3 parks to boost happiness above 40!',
    goal: (g) => countType(g, 'park') >= 3 && getStats(g).happiness > 40,
    fact: 'Central Park in NYC covers 843 acres and was designed in 1858. Green spaces reduce stress and improve air quality!' },
  { id: 6, title: 'Emergency Services', difficulty: 2, description: 'Build a hospital and keep happiness above 50!',
    goal: (g) => countType(g, 'hospital') >= 1 && getStats(g).happiness > 50,
    fact: 'The first public hospital was established in Baghdad in 805 CE. Today, WHO recommends 1 hospital per 100,000 people.' },
  { id: 7, title: 'Education Hub', difficulty: 2, description: 'Build 2 schools and maintain a positive budget!',
    goal: (g) => countType(g, 'school') >= 2 && getStats(g).income > 0,
    fact: 'Finland education system is ranked #1 globally. They have shorter school days, no homework, and no standardized tests!' },
  { id: 8, title: 'High-Density Living', difficulty: 2, description: 'Build 3 apartments and keep happiness above 30!',
    goal: (g) => countType(g, 'apartment') >= 3 && getStats(g).happiness > 30,
    fact: 'Hong Kong has the most skyscrapers in the world (>500). Kowloon Walled City once had 33,000 people in 2.6 hectares!' },
  { id: 9, title: 'Public Transit', difficulty: 2, description: 'Build 4 bus stops connected by roads. Reduce pollution below 20!',
    goal: (g) => countType(g, 'bus_stop') >= 4 && getStats(g).pollution < 20,
    fact: 'Curitiba, Brazil pioneered Bus Rapid Transit (BRT) in 1974. Good public transit can reduce car usage by 40%!' },
  { id: 10, title: 'Sustainable City', difficulty: 3, description: 'Build a city with positive power, happiness > 60, pollution < 15, and income > 500!',
    goal: (g) => { const s = getStats(g); return s.power >= 0 && s.happiness > 60 && s.pollution < 15 && s.income > 500; },
    fact: 'Masdar City in Abu Dhabi aims to be the world first zero-carbon, zero-waste city. Copenhagen plans to be carbon-neutral by 2025!' },
];

function countType(grid, type) {
  return grid.flat().filter(t => t === type).length;
}

function getStats(grid) {
  let happiness = 0, pollution = 0, income = 0, power = 0, cost = 0;
  grid.flat().forEach(t => {
    const tile = TILE_TYPES[t];
    happiness += tile.happiness;
    pollution += tile.pollution;
    income += tile.income;
    power += tile.power;
    cost += tile.cost;
  });
  return { happiness, pollution: Math.max(0, pollution), income, power, cost };
}

function createEmptyGrid() {
  return Array(GRID_SIZE).fill(null).map(() => Array(GRID_SIZE).fill('empty'));
}

const BUILDING_PALETTE = ['road','house','apartment','shop','factory','park','school','hospital','powerplant','bus_stop','solar'];

export default function CityBuilderLab() {
  const navigate = useNavigate();
  const [showMissions, setShowMissions] = useState(true);
  const { completedMissions, unlockedMissions, currentMission, setCurrentMission, completeMission: saveProgress, xp, loading: progressLoading } = useLabProgress('city-builder', 10);
  const [score, setScore] = useState(0);
  const [showFact, setShowFact] = useState(false);
  const [grid, setGrid] = useState(createEmptyGrid);
  const [selectedTool, setSelectedTool] = useState('house');
  const [budget, setBudget] = useState(5000);

  const mission = MISSIONS[currentMission];
  const stats = getStats(grid);

  const placeTile = (r, c) => {
    const tile = TILE_TYPES[selectedTool];
    if (selectedTool === 'empty') {
      // Remove/demolish
      const existing = grid[r][c];
      if (existing !== 'empty') {
        setBudget(b => b + Math.floor(TILE_TYPES[existing].cost * 0.5));
        setGrid(prev => { const ng = prev.map(row => [...row]); ng[r][c] = 'empty'; return ng; });
      }
      return;
    }
    if (budget < tile.cost) return;
    if (grid[r][c] !== 'empty') return;
    setBudget(b => b - tile.cost);
    setGrid(prev => { const ng = prev.map(row => [...row]); ng[r][c] = selectedTool; return ng; });
  };

  const goalMet = mission.goal(grid);
  const isCompleted = completedMissions.includes(currentMission);

  const completeMission = () => {
    if (!completedMissions.includes(currentMission)) {
      saveProgress(currentMission);
      setScore(s => s + mission.difficulty * 10);
    }
    setShowFact(true);
  };

  const resetMission = () => {
    setGrid(createEmptyGrid()); setBudget(5000); setShowFact(false);
  };

  if (showMissions) {
    return (
      <div className="min-h-screen bg-background text-foreground">
        <header className="sticky top-0 z-50 backdrop-blur-xl bg-background/80 border-b border-border px-4 py-3">
          <div className="max-w-6xl mx-auto flex items-center justify-between">
            <button onClick={() => navigate('/labs')} className="flex items-center gap-2 text-muted-foreground hover:text-foreground transition" data-testid="back-btn">
              <ArrowLeft className="w-4 h-4" /> Back
            </button>
            <h1 className="text-lg font-bold flex items-center gap-2"><Building2 className="w-5 h-5 text-cyan-400" /> City Builder</h1>
            <Badge variant="secondary" className="bg-cyan-500/20 text-cyan-300">{score} pts</Badge>
          </div>
        </header>
        <main className="max-w-6xl mx-auto p-6">
          <div className="mb-6">
            <h2 className="text-2xl font-bold mb-1">Choose a Mission</h2>
            <p className="text-sm text-muted-foreground">Plan, build, and manage your own city!</p>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3" data-testid="mission-grid">
            {MISSIONS.map((m, idx) => {
              const locked = !unlockedMissions.includes(idx);
              const done = completedMissions.includes(idx);
              return (
                <motion.button key={m.id} whileHover={!locked ? { scale: 1.04 } : {}} whileTap={!locked ? { scale: 0.97 } : {}}
                  onClick={() => { if (!locked) { setCurrentMission(idx); setShowMissions(false); resetMission(); } }}
                  disabled={locked}
                  className={`relative p-4 rounded-2xl border text-left transition-all ${
                    done ? 'border-emerald-500/30 bg-emerald-500/5' :
                    locked ? 'border-border bg-card opacity-70 cursor-not-allowed' :
                    'border-border bg-card hover:border-cyan-500/30 hover:bg-cyan-500/5'
                  }`}
                  data-testid={`mission-${idx}`}>
                  {locked && <Lock className="absolute top-3 right-3 w-3 h-3 text-muted-foreground/30" />}
                  {done && <Check className="absolute top-3 right-3 w-4 h-4 text-emerald-400" />}
                  <span className="text-[10px] text-foreground uppercase tracking-widest">Mission {idx + 1}</span>
                  <p className="text-sm font-semibold mt-1 text-foreground leading-tight">{m.title}</p>
                  <div className="flex items-center gap-0.5 mt-2">
                    {[...Array(3)].map((_, i) => <Star key={i} className={`w-3 h-3 ${i < m.difficulty ? 'text-amber-400 fill-amber-400' : 'text-muted-foreground/20'}`} />)}
                  </div>
                </motion.button>
              );
            })}
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background text-foreground">
      <header className="sticky top-0 z-50 backdrop-blur-xl bg-background/80 border-b border-border px-4 py-3">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <button onClick={() => setShowMissions(true)} className="flex items-center gap-2 text-muted-foreground hover:text-foreground transition" data-testid="back-to-missions">
            <ArrowLeft className="w-4 h-4" /> Missions
          </button>
          <h1 className="text-sm font-semibold text-foreground/80">{mission.title}</h1>
          <div className="flex items-center gap-3">
            <span className="text-xs text-muted-foreground/70">{score} pts</span>
            <span className="text-xs text-muted-foreground/70">{completedMissions.length}/10</span>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto p-4" data-testid="city-builder">
        <div className="grid grid-cols-1 lg:grid-cols-[1fr_300px] gap-6">
          <div className="space-y-4">
            {/* Stats Bar */}
            <div className="grid grid-cols-5 gap-2" data-testid="stats-bar">
              <div className="p-2 rounded-xl border border-amber-500/20 bg-amber-500/5 text-center">
                <p className="text-[10px] text-amber-400">Budget</p>
                <p className="text-sm font-bold text-amber-300">${budget.toLocaleString()}</p>
              </div>
              <div className="p-2 rounded-xl border border-green-500/20 bg-green-500/5 text-center">
                <p className="text-[10px] text-green-400">Happiness</p>
                <p className={`text-sm font-bold ${stats.happiness > 0 ? 'text-green-300' : 'text-red-300'}`}>{stats.happiness}</p>
              </div>
              <div className="p-2 rounded-xl border border-red-500/20 bg-red-500/5 text-center">
                <p className="text-[10px] text-red-400">Pollution</p>
                <p className={`text-sm font-bold ${stats.pollution < 20 ? 'text-green-300' : 'text-red-300'}`}>{stats.pollution}</p>
              </div>
              <div className="p-2 rounded-xl border border-blue-500/20 bg-blue-500/5 text-center">
                <p className="text-[10px] text-blue-400">Income/mo</p>
                <p className={`text-sm font-bold ${stats.income > 0 ? 'text-blue-300' : 'text-red-300'}`}>${stats.income}</p>
              </div>
              <div className="p-2 rounded-xl border border-yellow-500/20 bg-yellow-500/5 text-center">
                <p className="text-[10px] text-yellow-400">Power</p>
                <p className={`text-sm font-bold ${stats.power >= 0 ? 'text-yellow-300' : 'text-red-300'}`}>{stats.power}MW</p>
              </div>
            </div>

            {/* Building Palette */}
            <div className="flex flex-wrap gap-1.5 p-3 rounded-2xl border border-border bg-card" data-testid="palette">
              <button onClick={() => setSelectedTool('empty')}
                className={`px-2 py-1 rounded-lg text-[10px] font-semibold flex items-center gap-1 transition ${selectedTool === 'empty' ? 'bg-red-500/20 text-red-300 border border-red-500/30' : 'bg-muted/50 text-muted-foreground border border-transparent hover:bg-muted'}`}
                data-testid="tool-demolish">
                <Trash2 className="w-3 h-3" /> Demolish
              </button>
              {BUILDING_PALETTE.map(type => {
                const tile = TILE_TYPES[type];
                const IconComp = tile.icon;
                return (
                  <button key={type} onClick={() => setSelectedTool(type)}
                    className={`px-2 py-1 rounded-lg text-[10px] font-semibold flex items-center gap-1 transition ${selectedTool === type ? 'border' : 'bg-muted/50 text-muted-foreground border border-transparent hover:bg-muted'}`}
                    style={selectedTool === type ? { backgroundColor: tile.color + '20', color: tile.color, borderColor: tile.color + '50' } : {}}
                    data-testid={`tool-${type}`}>
                    {IconComp && <IconComp className="w-3 h-3" />}
                    {tile.label} (${tile.cost})
                  </button>
                );
              })}
            </div>

            {/* Grid */}
            <div className="p-3 rounded-2xl border border-border bg-card" data-testid="city-grid">
              <div className="inline-grid gap-1" style={{ gridTemplateColumns: `repeat(${GRID_SIZE}, 1fr)` }}>
                {grid.map((row, r) =>
                  row.map((cell, c) => {
                    const tile = TILE_TYPES[cell];
                    const IconComp = tile.icon;
                    return (
                      <motion.button key={`${r}-${c}`}
                        whileHover={{ scale: 1.08 }} whileTap={{ scale: 0.95 }}
                        onClick={() => placeTile(r, c)}
                        className="w-12 h-12 sm:w-14 sm:h-14 rounded-lg border flex items-center justify-center transition-all"
                        style={{
                          backgroundColor: cell === 'empty' ? 'rgba(255,255,255,0.02)' : tile.color + '30',
                          borderColor: cell === 'empty' ? 'rgba(255,255,255,0.05)' : tile.color + '50',
                        }}
                        data-testid={`cell-${r}-${c}`}>
                        {IconComp && <IconComp className="w-4 h-4" style={{ color: tile.color }} />}
                        {cell === 'road' && <div className="w-8 h-1 rounded bg-gray-500" />}
                      </motion.button>
                    );
                  })
                )}
              </div>
            </div>

            {/* Actions */}
            <div className="flex gap-3">
              {goalMet && !isCompleted && (
                <Button onClick={completeMission} className="bg-gradient-to-r from-emerald-500 to-teal-600" data-testid="complete-btn">
                  <Check className="w-4 h-4 mr-1" /> Goal Achieved!
                </Button>
              )}
              <Button variant="outline" onClick={resetMission} className="border-white/20 text-muted-foreground" data-testid="reset-btn">
                <RotateCcw className="w-4 h-4 mr-1" /> Clear City
              </Button>
            </div>

            <AnimatePresence>
              {showFact && (
                <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
                  className="p-4 rounded-2xl border border-cyan-500/20 bg-cyan-500/5" data-testid="city-fact">
                  <p className="text-xs text-cyan-400 font-semibold flex items-center gap-1 mb-1"><Info className="w-3 h-3" /> Urban Planning Fact</p>
                  <p className="text-sm text-foreground/80">{mission.fact}</p>
                  {currentMission < MISSIONS.length - 1 && (
                    <Button size="sm" onClick={() => { setCurrentMission(p => p + 1); resetMission(); }}
                      className="mt-3 bg-cyan-600 hover:bg-cyan-500" data-testid="next-mission-btn">
                      Next Mission <ChevronRight className="w-4 h-4 ml-1" />
                    </Button>
                  )}
                </motion.div>
              )}
            </AnimatePresence>
          </div>

          {/* Sidebar */}
          <div className="space-y-4 lg:sticky lg:top-[65px] lg:self-start">
            <div className="p-4 rounded-2xl border border-border bg-card">
              <h3 className="font-semibold text-sm mb-2 flex items-center gap-1.5"><Building2 className="w-4 h-4 text-cyan-400" /> Objective</h3>
              <p className="text-xs text-muted-foreground leading-relaxed">{mission.description}</p>
            </div>
            <div className="p-4 rounded-2xl border border-border bg-card">
              <h3 className="font-semibold text-sm mb-2">How to Play</h3>
              <ol className="text-xs text-muted-foreground space-y-1.5 list-decimal list-inside">
                <li>Select a building from the palette above</li>
                <li>Click an empty cell to place it</li>
                <li>Watch your stats change in real-time</li>
                <li>Meet the mission goal to advance!</li>
                <li>Use Demolish to remove and get 50% refund</li>
              </ol>
            </div>
            <div className="p-4 rounded-2xl border border-border bg-card">
              <h3 className="font-semibold text-sm mb-2">Progress</h3>
              <div className="space-y-1.5">
                {MISSIONS.map((m, idx) => (
                  <button key={m.id} onClick={() => { if (unlockedMissions.includes(idx)) { setCurrentMission(idx); setShowMissions(false); resetMission(); } }}
                    disabled={!unlockedMissions.includes(idx)}
                    className={`w-full flex items-center gap-2 px-2 py-1.5 rounded-lg text-xs transition-all ${
                      idx === currentMission ? 'bg-primary text-primary-foreground' :
                      completedMissions.includes(idx) ? 'text-emerald-400 hover:bg-muted/50' :
                      unlockedMissions.includes(idx) ? 'text-muted-foreground hover:bg-muted/50' : 'text-muted-foreground/30 cursor-not-allowed'
                    }`}>
                    <span className={`w-5 h-5 rounded-md flex items-center justify-center text-[10px] font-bold ${
                      completedMissions.includes(idx) ? 'bg-emerald-500 text-white' :
                      idx === currentMission ? 'bg-primary text-primary-foreground' : 'bg-muted/50'
                    }`}>
                      {completedMissions.includes(idx) ? <Check className="w-3 h-3" /> : unlockedMissions.includes(idx) ? idx + 1 : <Lock className="w-2.5 h-2.5" />}
                    </span>
                    <span className="truncate">{m.title}</span>
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}
